import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Shield, Loader2, Globe, ArrowLeft, CheckCircle, CreditCard, QrCode, Building2, RefreshCw, Mail, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// List of countries for nationality dropdown
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria",
  "Bangladesh", "Belgium", "Brazil", "Brunei", "Cambodia", "Cameroon", "Canada",
  "Chile", "China", "Colombia", "Croatia", "Czech Republic", "Denmark",
  "Egypt", "Ethiopia", "Finland", "France", "Germany", "Ghana", "Greece",
  "Hong Kong", "Hungary", "India", "Indonesia", "Iran", "Iraq", "Ireland",
  "Israel", "Italy", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kuwait",
  "Laos", "Lebanon", "Libya", "Malaysia", "Maldives", "Mexico", "Mongolia",
  "Morocco", "Myanmar", "Nepal", "Netherlands", "New Zealand", "Nigeria",
  "North Korea", "Norway", "Oman", "Pakistan", "Palestine", "Peru", "Philippines",
  "Poland", "Portugal", "Qatar", "Romania", "Russia", "Saudi Arabia", "Serbia",
  "Singapore", "Slovakia", "South Africa", "South Korea", "Spain", "Sri Lanka",
  "Sudan", "Sweden", "Switzerland", "Syria", "Taiwan", "Tanzania", "Thailand",
  "Tunisia", "Turkey", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom",
  "United States", "Uzbekistan", "Venezuela", "Vietnam", "Yemen", "Zimbabwe"
];

// Translations for email verification messages
const TRANSLATIONS = {
  en: {
    checkEmail: "Please check your email and click the verification link to activate your account.",
    emailSent: "Verification email sent!",
    resend: "Resend verification email",
    resendSuccess: "Verification email resent. Please check your inbox.",
    backToLogin: "Back to Login",
    didntReceive: "Didn't receive the email?",
    checkSpam: "Check your spam folder or click below to resend.",
    verifying: "Verifying your email...",
    verificationSuccess: "Your email has been verified. You can now log in.",
    verificationFailed: "Verification failed. The link may have expired.",
    redirecting: "Redirecting to login...",
    tooManyRequests: "Too many requests. Please wait before trying again.",
    emailNotVerified: "Email not verified. Please check your email and click the verification link.",
    register: "Register",
    createAccount: "Create Your Account",
    fillDetails: "Fill in your details to join MyShield"
  },
  ms: {
    checkEmail: "Sila semak emel anda dan klik pautan pengesahan untuk mengaktifkan akaun anda.",
    emailSent: "Emel pengesahan dihantar!",
    resend: "Hantar semula emel pengesahan",
    resendSuccess: "Emel pengesahan dihantar semula. Sila semak peti masuk anda.",
    backToLogin: "Kembali ke Log Masuk",
    didntReceive: "Tidak menerima emel?",
    checkSpam: "Semak folder spam anda atau klik di bawah untuk hantar semula.",
    verifying: "Mengesahkan emel anda...",
    verificationSuccess: "Emel anda telah disahkan. Anda boleh log masuk sekarang.",
    verificationFailed: "Pengesahan gagal. Pautan mungkin telah tamat tempoh.",
    redirecting: "Mengalihkan ke log masuk...",
    tooManyRequests: "Terlalu banyak permintaan. Sila tunggu sebelum mencuba lagi.",
    emailNotVerified: "Emel tidak disahkan. Sila semak emel anda dan klik pautan pengesahan.",
    register: "Daftar",
    createAccount: "Cipta Akaun Anda",
    fillDetails: "Isi butiran anda untuk menyertai MyShield"
  },
  ta: {
    checkEmail: "உங்கள் மின்னஞ்சலைச் சரிபார்த்து, உங்கள் கணக்கை செயல்படுத்த சரிபார்ப்பு இணைப்பைக் கிளிக் செய்யுங்கள்.",
    emailSent: "சரிபார்ப்பு மின்னஞ்சல் அனுப்பப்பட்டது!",
    resend: "சரிபார்ப்பு மின்னஞ்சலை மீண்டும் அனுப்பு",
    resendSuccess: "சரிபார்ப்பு மின்னஞ்சல் மீண்டும் அனுப்பப்பட்டது. உங்கள் இன்பாக்ஸை சரிபார்க்கவும்.",
    backToLogin: "உள்நுழைவுக்குத் திரும்பு",
    didntReceive: "மின்னஞ்சல் கிடைக்கவில்லையா?",
    checkSpam: "உங்கள் ஸ்பேம் கோப்புறையை சரிபார்க்கவும் அல்லது மீண்டும் அனுப்ப கீழே கிளிக் செய்யவும்.",
    verifying: "உங்கள் மின்னஞ்சலை சரிபார்க்கிறது...",
    verificationSuccess: "உங்கள் மின்னஞ்சல் சரிபார்க்கப்பட்டது. நீங்கள் இப்போது உள்நுழையலாம்.",
    verificationFailed: "சரிபார்ப்பு தோல்வியடைந்தது. இணைப்பு காலாவதியாகியிருக்கலாம்.",
    redirecting: "உள்நுழைவுக்கு திருப்பிவிடுகிறது...",
    tooManyRequests: "அதிக கோரிக்கைகள். மீண்டும் முயற்சிக்கும் முன் காத்திருக்கவும்.",
    emailNotVerified: "மின்னஞ்சல் சரிபார்க்கப்படவில்லை. உங்கள் மின்னஞ்சலை சரிபார்த்து சரிபார்ப்பு இணைப்பைக் கிளிக் செய்யுங்கள்.",
    register: "பதிவு செய்",
    createAccount: "உங்கள் கணக்கை உருவாக்குங்கள்",
    fillDetails: "MyShield இல் சேர உங்கள் விவரங்களை நிரப்பவும்"
  },
  hi: {
    checkEmail: "कृपया अपना ईमेल जांचें और अपना खाता सक्रिय करने के लिए सत्यापन लिंक पर क्लिक करें।",
    emailSent: "सत्यापन ईमेल भेजा गया!",
    resend: "सत्यापन ईमेल पुनः भेजें",
    resendSuccess: "सत्यापन ईमेल पुनः भेजा गया। कृपया अपना इनबॉक्स जांचें।",
    backToLogin: "लॉगिन पर वापस जाएं",
    didntReceive: "ईमेल नहीं मिला?",
    checkSpam: "अपना स्पैम फोल्डर जांचें या पुनः भेजने के लिए नीचे क्लिक करें।",
    verifying: "आपका ईमेल सत्यापित हो रहा है...",
    verificationSuccess: "आपका ईमेल सत्यापित हो गया है। अब आप लॉग इन कर सकते हैं।",
    verificationFailed: "सत्यापन विफल। लिंक समाप्त हो सकता है।",
    redirecting: "लॉगिन पर रीडायरेक्ट हो रहा है...",
    tooManyRequests: "बहुत सारे अनुरोध। कृपया पुनः प्रयास करने से पहले प्रतीक्षा करें।",
    emailNotVerified: "ईमेल सत्यापित नहीं है। कृपया अपना ईमेल जांचें और सत्यापन लिंक पर क्लिक करें।",
    register: "रजिस्टर करें",
    createAccount: "अपना खाता बनाएं",
    fillDetails: "MyShield में शामिल होने के लिए अपना विवरण भरें"
  },
  bn: {
    checkEmail: "অনুগ্রহ করে আপনার ইমেল চেক করুন এবং আপনার অ্যাকাউন্ট সক্রিয় করতে যাচাইকরণ লিঙ্কে ক্লিক করুন।",
    emailSent: "যাচাইকরণ ইমেল পাঠানো হয়েছে!",
    resend: "যাচাইকরণ ইমেল পুনরায় পাঠান",
    resendSuccess: "যাচাইকরণ ইমেল পুনরায় পাঠানো হয়েছে। অনুগ্রহ করে আপনার ইনবক্স চেক করুন।",
    backToLogin: "লগইনে ফিরে যান",
    didntReceive: "ইমেল পাননি?",
    checkSpam: "আপনার স্প্যাম ফোল্ডার চেক করুন বা পুনরায় পাঠাতে নিচে ক্লিক করুন।",
    verifying: "আপনার ইমেল যাচাই করা হচ্ছে...",
    verificationSuccess: "আপনার ইমেল যাচাই করা হয়েছে। আপনি এখন লগ ইন করতে পারেন।",
    verificationFailed: "যাচাইকরণ ব্যর্থ। লিঙ্কের মেয়াদ শেষ হয়ে থাকতে পারে।",
    redirecting: "লগইনে রিডাইরেক্ট করা হচ্ছে...",
    tooManyRequests: "অনেক বেশি অনুরোধ। আবার চেষ্টা করার আগে অনুগ্রহ করে অপেক্ষা করুন।",
    emailNotVerified: "ইমেল যাচাই করা হয়নি। অনুগ্রহ করে আপনার ইমেল চেক করুন এবং যাচাইকরণ লিঙ্কে ক্লিক করুন।",
    register: "নিবন্ধন করুন",
    createAccount: "আপনার অ্যাকাউন্ট তৈরি করুন",
    fillDetails: "MyShield-এ যোগ দিতে আপনার বিবরণ পূরণ করুন"
  },
  ne: {
    checkEmail: "कृपया आफ्नो इमेल जाँच गर्नुहोस् र आफ्नो खाता सक्रिय गर्न प्रमाणीकरण लिंकमा क्लिक गर्नुहोस्।",
    emailSent: "प्रमाणीकरण इमेल पठाइयो!",
    resend: "प्रमाणीकरण इमेल पुन: पठाउनुहोस्",
    resendSuccess: "प्रमाणीकरण इमेल पुन: पठाइयो। कृपया आफ्नो इनबक्स जाँच गर्नुहोस्।",
    backToLogin: "लगइनमा फर्कनुहोस्",
    didntReceive: "इमेल प्राप्त भएन?",
    checkSpam: "आफ्नो स्प्याम फोल्डर जाँच गर्नुहोस् वा पुन: पठाउन तल क्लिक गर्नुहोस्।",
    verifying: "तपाईंको इमेल प्रमाणित गर्दै...",
    verificationSuccess: "तपाईंको इमेल प्रमाणित भयो। अब तपाईं लग इन गर्न सक्नुहुन्छ।",
    verificationFailed: "प्रमाणीकरण असफल। लिंक समाप्त भएको हुन सक्छ।",
    redirecting: "लगइनमा रिडाइरेक्ट गर्दै...",
    tooManyRequests: "धेरै अनुरोधहरू। कृपया पुन: प्रयास गर्नु अघि पर्खनुहोस्।",
    emailNotVerified: "इमेल प्रमाणित छैन। कृपया आफ्नो इमेल जाँच गर्नुहोस् र प्रमाणीकरण लिंकमा क्लिक गर्नुहोस्।",
    register: "दर्ता गर्नुहोस्",
    createAccount: "तपाईंको खाता बनाउनुहोस्",
    fillDetails: "MyShield मा सामेल हुन आफ्नो विवरण भर्नुहोस्"
  },
  my: {
    checkEmail: "သင့်အီးမေးလ်ကို စစ်ဆေးပြီး သင့်အကောင့်ကို အသက်သွင်းရန် အတည်ပြုလင့်ခ်ကို နှိပ်ပါ။",
    emailSent: "အတည်ပြုအီးမေးလ် ပို့ပြီးပါပြီ!",
    resend: "အတည်ပြုအီးမေးလ် ပြန်ပို့ပါ",
    resendSuccess: "အတည်ပြုအီးမေးလ် ပြန်ပို့ပြီးပါပြီ။ သင့် inbox ကို စစ်ဆေးပါ။",
    backToLogin: "လော့ဂ်အင်သို့ ပြန်သွားပါ",
    didntReceive: "အီးမေးလ် မရရှိဘူးလား?",
    checkSpam: "သင့် spam ဖိုဒါကို စစ်ဆေးပါ သို့မဟုတ် ပြန်ပို့ရန် အောက်တွင် နှိပ်ပါ။",
    verifying: "သင့်အီးမေးလ်ကို အတည်ပြုနေသည်...",
    verificationSuccess: "သင့်အီးမေးလ်ကို အတည်ပြုပြီးပါပြီ။ ယခု လော့ဂ်အင်ဝင်နိုင်ပါပြီ။",
    verificationFailed: "အတည်ပြုခြင်း မအောင်မြင်ပါ။ လင့်ခ် သက်တမ်းကုန်သွားနိုင်သည်။",
    redirecting: "လော့ဂ်အင်သို့ ပြန်ညွှန်းနေသည်...",
    tooManyRequests: "တောင်းဆိုမှု များလွန်းသည်။ ထပ်မကြိုးစားမီ စောင့်ပါ။",
    emailNotVerified: "အီးမေးလ် အတည်မပြုရသေးပါ။ သင့်အီးမေးလ်ကို စစ်ဆေးပြီး အတည်ပြုလင့်ခ်ကို နှိပ်ပါ။",
    register: "မှတ်ပုံတင်ပါ",
    createAccount: "သင့်အကောင့်ကို ဖန်တီးပါ",
    fillDetails: "MyShield တွင် ပါဝင်ရန် သင့်အချက်အလက်များ ဖြည့်ပါ"
  }
};

// List of supported languages
const LANGUAGES = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "ms", name: "Bahasa Melayu", flag: "🇲🇾" },
  { code: "ta", name: "தமிழ் (Tamil)", flag: "🇮🇳" },
  { code: "hi", name: "हिन्दी (Hindi)", flag: "🇮🇳" },
  { code: "bn", name: "বাংলা (Bengali)", flag: "🇧🇩" },
  { code: "ne", name: "नेपाली (Nepali)", flag: "🇳🇵" },
  { code: "my", name: "မြန်မာ (Burmese)", flag: "🇲🇲" },
];

export default function RegisterPage({ setUser }) {
  const [step, setStep] = useState(1); // 1: Form, 2: Check Email
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    role: "member",
    phone: "",
    passport_number: "",
    nationality: "",
    employer: "",
    visa_expiry: "",
    preferred_language: "en",
  });
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const benefitParam = searchParams.get('benefit');
  const lang = formData.preferred_language || "en";
  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;

  // Pre-fill language from localStorage if user came from landing page
  useEffect(() => {
    const savedLang = localStorage.getItem('myshield_lang');
    if (savedLang && TRANSLATIONS[savedLang]) {
      setFormData(prev => ({ ...prev, preferred_language: savedLang }));
    }
  }, []);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.email || !formData.password || !formData.full_name || !formData.phone || !formData.passport_number || !formData.nationality) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, formData);

      if (response.data.requires_verification) {
        // Show the "Check your email" screen
        localStorage.setItem("myshield_lang", formData.preferred_language);
        setStep(2);
        toast.success(t.emailSent);
      } else {
        // Admin registration - log them in directly
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        localStorage.setItem("myshield_lang", formData.preferred_language);
        setUser(response.data.user);
        navigate("/admin");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendTimer > 0) return;
    
    setResendLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, {
        email: formData.email
      });
      toast.success(t.resendSuccess);
      setResendTimer(60); // 60 second cooldown
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(t.tooManyRequests);
      } else {
        toast.error(error.response?.data?.detail || "Failed to resend email");
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-[#0A1128] to-black p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>
      
      {/* Back Button */}
      <Link 
        to="/" 
        className="absolute top-4 left-4 flex items-center gap-2 text-white/60 hover:text-white transition-colors z-20"
        data-testid="back-to-home"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        <span className="text-sm font-medium">Back to Home</span>
      </Link>

      {step === 1 ? (
        // Registration Form
        <div className="w-full max-w-md glass-card p-8 relative z-10 animate-fade-in" data-testid="register-card">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_protect-workers/artifacts/awekt278_image.png" 
              alt="MyShield Logo" 
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-white">{t.createAccount}</h1>
            <p className="text-white/50 text-sm mt-2">{t.fillDetails}</p>
          </div>
          
          <form onSubmit={handleRegister} className="space-y-4">
            {/* Language Selector */}
            <div className="space-y-2">
              <Label className="text-white/70">Language / Bahasa</Label>
              <Select value={formData.preferred_language} onValueChange={(val) => setFormData(prev => ({ ...prev, preferred_language: val }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {LANGUAGES.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code} className="text-white">
                      <span className="mr-2">{lang.flag}</span> {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Full Name */}
            <div className="space-y-2">
              <Label className="text-white/70">Full Name *</Label>
              <Input
                name="full_name"
                placeholder="Enter your full name"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                data-testid="register-name"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label className="text-white/70">Email *</Label>
              <Input
                name="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                data-testid="register-email"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label className="text-white/70">Password *</Label>
              <Input
                name="password"
                type="password"
                placeholder="At least 6 characters"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                data-testid="register-password"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label className="text-white/70">Phone Number *</Label>
              <Input
                name="phone"
                placeholder="+60 12 345 6789"
                value={formData.phone}
                onChange={handleChange}
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                data-testid="register-phone"
              />
            </div>

            {/* Passport */}
            <div className="space-y-2">
              <Label className="text-white/70">Passport Number *</Label>
              <Input
                name="passport_number"
                placeholder="Enter passport number"
                value={formData.passport_number}
                onChange={handleChange}
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                data-testid="register-passport"
              />
            </div>

            {/* Nationality */}
            <div className="space-y-2">
              <Label className="text-white/70">Nationality *</Label>
              <Select value={formData.nationality} onValueChange={(val) => setFormData(prev => ({ ...prev, nationality: val }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-xl" data-testid="register-nationality">
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10 max-h-60">
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country} value={country} className="text-white">{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Employer (Optional) */}
            <div className="space-y-2">
              <Label className="text-white/70">Employer (Optional)</Label>
              <Input
                name="employer"
                placeholder="Company name"
                value={formData.employer}
                onChange={handleChange}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                data-testid="register-employer"
              />
            </div>

            <Button 
              type="submit"
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold py-6 rounded-xl transition-all active:scale-[0.98]"
              disabled={loading}
              data-testid="register-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {t.register}
                </>
              )}
            </Button>
            
            <p className="text-center text-sm text-white/50">
              Already have an account?{" "}
              <Link to="/login" className="text-cyan-400 hover:text-cyan-300" data-testid="login-link">
                Login
              </Link>
            </p>
          </form>
        </div>
      ) : (
        // Step 2: Check Your Email Screen
        <div className="w-full max-w-md glass-card p-8 relative z-10 animate-fade-in text-center" data-testid="check-email-card">
          <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-cyan-400" strokeWidth={1.5} />
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-3">{t.emailSent}</h1>
          
          <p className="text-white/60 mb-6">
            {t.checkEmail}
          </p>
          
          <div className="bg-white/5 rounded-xl p-4 mb-4">
            <p className="text-sm text-white/50 mb-1">Email sent to:</p>
            <p className="text-white font-medium">{formData.email}</p>
          </div>
          
          {/* Important: Check Spam Notice */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6">
            <p className="text-amber-400 text-sm font-medium mb-1">Important!</p>
            <p className="text-white/60 text-sm">
              Please check your <strong className="text-white">Spam/Junk folder</strong> if you don't see the email in your inbox.
            </p>
          </div>
          
          <div className="space-y-4">
            <Button
              onClick={handleResendVerification}
              variant="outline"
              className="w-full border-white/10 text-white hover:bg-white/5 rounded-xl"
              disabled={resendLoading || resendTimer > 0}
              data-testid="resend-email-button"
            >
              {resendLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : resendTimer > 0 ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t.resend} ({resendTimer}s)
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t.resend}
                </>
              )}
            </Button>
            
            <Link to="/login">
              <Button
                variant="ghost"
                className="w-full text-white/60 hover:text-white hover:bg-white/5 rounded-xl"
                data-testid="back-to-login-button"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t.backToLogin}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
